import { useParams, Link } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import Moment from "moment";
import "moment-timezone";
import { toast } from "react-toastify";
import { GetTransactionQuery } from "../../gql/graphql";
import ReactJson from "react-json-view";
import { ShareparkDateTime } from "../../components/LocalTime";

const GET = gql`
  query GetTransaction($transactionId: UUID!) {
    transactionById(id: $transactionId) {
      id
      type
      transactionType
      startUtc
      endUtc
      area {
        id
        name
      }
      site {
        id
        name
      }
      reservation {
        id
        startDate
        startTime
        endDate
        endTime
        name
        email
        mobile
        plate
        parkerId
        vehicleId
      }
      parker {
        id
        firstName
        lastName
      }
      vehicle {
        id
        plate
      }
      siteMessages {
        id
        type
        payload
        response
        duration
        createdUtc
      }
      events {
        id
        type
        message
        createdUtc
      }
      behaviorViolations {
        id
        violationType
        detectedUtc
        escalationScheduledUtc
        resolvedUtc
        status
        description
        escalationNotes
      }
      invoices {
        id
        createdUtc
        amount
        status
        stripeInvoiceId
        stripeInvoiceNumber
        stripeInvoicePdfUrl
        stripeError
        stripeErrorUtc
        invoiceItems {
          id
          amount
          description
          quantity
          unitPrice
          stripeId
        }
      }
    }
  }
`;

// const CLEARSITEDATA = gql`
// mutation ClearSiteData($siteId: UUID!) {
//     clearSiteData(siteId: $siteId){
//         id
//     }
// }
// `;

const RESET_TRANSACTION = gql`
  mutation ResetTransaction($transactionId: UUID!) {
    resetTransaction(transactionId: $transactionId) {
      id
    }
  }
`;

const TERMINATE_TRANSACTION = gql`
  mutation TerminateTransaction($transactionId: UUID!) {
    terminateTransaction(transactionId: $transactionId) {
      id
      endUtc
      isOnSite
    }
  }
`;

const RETRY_STRIPE_INVOICE = gql`
  mutation RetryStripeInvoice($invoiceId: UUID!) {
    retryStripeInvoice(invoiceId: $invoiceId) {
      id
      stripeError
    }
  }
`;

export const Transaction = () => {
  let { transactionId } = useParams();

  const { data, refetch } = useQuery<GetTransactionQuery>(GET, {
    variables: {
      transactionId,
    },
    fetchPolicy: "no-cache",
  });
  const [resetTransaction] = useMutation(RESET_TRANSACTION, {
    variables: { transactionId },
  });
  const [terminateTransaction] = useMutation(TERMINATE_TRANSACTION, {
    variables: { transactionId },
  });

  const [retryStripeInvoice, { loading: retrying }] =
    useMutation(RETRY_STRIPE_INVOICE);

  const transaction = data?.transactionById;

  const retryInvoice = async (invoiceId: string) => {
    let result;
    try {
      const x = await retryStripeInvoice({ variables: { invoiceId } });
      result = x.data?.retryStripeInvoice;
    } catch (e: any) {
      toast(`Retry failed: ${e.message}`, { type: toast.TYPE.ERROR });
      return;
    }
    if (result) {
      if (result.stripeError) {
        toast(`Retry failed: ${result.stripeError}`, {
          type: toast.TYPE.ERROR,
        });
      } else {
        toast(`Invoice sent to Stripe`, {
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
        });
      }

      await refetch();
    }
  };

  const resetThisTransaction = async () => {
    const x = await resetTransaction({ variables: { transactionId } });
    if (x.data.resetTransaction) {
      toast(`Transaction payment is reset`, {
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });

      await refetch();
    }
  };

  const terminateThisTransaction = async () => {
    if (!window.confirm('Are you sure you want to force close this transaction? This will set the end time to now and mark the parker as off-site.')) {
      return;
    }
    
    const x = await terminateTransaction({ variables: { transactionId } });
    if (x.data.terminateTransaction) {
      toast(`Transaction has been terminated`, {
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });

      await refetch();
    }
  };

  return (
    <>
      {transaction && (
        <div className="ms-md-2">
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
            <h1 className="h2">Transaction</h1>
          </div>

          {transaction.invoices && transaction.invoices.length > 0 && (
            <div className="mb-3 alert alert-danger row">
              <div className="col">
                <div>Redo Invoicing for this transaction</div>
                <div>
                  This removes the existing invoice and line items, then
                  recalculates and reinvoices.
                </div>
              </div>
              <div className="col">
                <button
                  className="btn btn-danger float-end"
                  onClick={() => resetThisTransaction()}
                >
                  Reset Transaction
                </button>
              </div>
            </div>
          )}

          {!transaction.endUtc && (
            <div className="mb-3 alert alert-warning row">
              <div className="col">
                <div>Force Close Transaction</div>
                <div>
                  This will immediately end the transaction, set the end time to now,
                  clear the on-site status, and add an admin event.
                </div>
              </div>
              <div className="col">
                <button
                  className="btn btn-warning float-end"
                  onClick={() => terminateThisTransaction()}
                >
                  Force Close
                </button>
              </div>
            </div>
          )}

          {transaction?.site && (
            <div className="row p-3 border rounded bg-light mb-3">
              <div className="col-md-2 text-md-end">Site:</div>
              <div className="col-md-10 fw-bold">
                <Link to={"/sites/" + transaction.site.id}>
                  {transaction.site.name}
                </Link>
              </div>
              {transaction?.area && (
                <div className="row mt-2">
                  <div className="col-md-2 text-md-end">Area:</div>
                  <div className="col-md-10 fw-bold">
                    {transaction.area.name}
                  </div>
                </div>
              )}
            </div>
          )}

          {transaction?.reservation && (
            <div className="row p-3 border rounded bg-light mb-3">
              <div className="col-md-2 text-md-end">Reservation:</div>
              <div className="col-md-10 fw-bold">
                <Link to={"/reservations/" + transaction.reservation.id}>
                  {transaction.reservation.startDate}{" "}
                  {transaction.reservation.startTime} to{" "}
                  {transaction.reservation.endDate}{" "}
                  {transaction.reservation.endTime}
                </Link>
              </div>
            </div>
          )}

          {transaction.parker && (
            <div className="row p-3 border rounded bg-light mb-3">
              <div className="col-md-2 text-md-end">
                {transaction.type === "STRATA_RESERVATION" ? "Booked by:" : "Parker:"}
              </div>
              <div className="col-md-10 fw-bold">
                <Link to={"/parkers/" + transaction.parker.id}>
                  {transaction.parker.firstName} {transaction.parker.lastName}
                </Link>
                {transaction.type === "STRATA_RESERVATION" && (
                  <span className="ms-2 badge bg-secondary">Strata booking</span>
                )}
              </div>
            </div>
          )}

          {(transaction.vehicle || transaction.reservation?.plate) && (
            <div className="row p-3 border rounded bg-light mb-3">
              <div className="col-md-2 text-md-end">Vehicle:</div>
              <div className="col-md-10 fw-bold">
                {transaction.vehicle ? (
                  <Link to={"/vehicles/" + transaction.vehicle.id}>
                    {transaction.vehicle.plate}
                  </Link>
                ) : (
                  <span>{transaction.reservation?.plate}</span>
                )}
              </div>
            </div>
          )}

          {transaction.invoices && transaction.invoices.length > 0 && (
            <div className="row p-3 border rounded bg-light mb-3">
              <div className="col-md-2 text-md-end">Invoices:</div>
              <div className="col-md-10">
                {/* <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Date</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Status</th>
                      <th scope="col">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transaction.invoices.map((invoice, index) => {
                      return (
                        <tr key={index}>
                          <th scope="row">{invoice?.stripeInvoiceNumber}</th>
                          <td>
                            {Moment.utc(invoice?.createdUtc?.substring(0, 19))
                              .tz(Moment.tz.guess())
                              .format("DD MMM YYYY HH:mm:ss")}
                          </td>
                          <td>${invoice?.amount.toFixed(2)}</td>
                          <td>{invoice?.status}</td>
                          <td>
                            {invoice?.stripeInvoicePdfUrl && (
                              <a href={invoice?.stripeInvoicePdfUrl}>
                                DOWNLOAD {invoice?.stripeInvoiceNumber}
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table> */}

                <div>
                  {transaction.invoices.map((invoice, index) => {
                    return (
                      <div key={index}>
                        <div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <div className="row">
                                <div className="col">
                                  Invoice {invoice?.stripeInvoiceNumber}
                                </div>
                                <div className="col text-end">
                                  {Moment.utc(
                                    invoice?.createdUtc?.substring(0, 19)
                                  )
                                    .tz(Moment.tz.guess())
                                    .format("DD MMM YYYY HH:mm:ss")}
                                </div>
                              </div>
                            </div>
                            <div className="card-body">
                              {invoice?.stripeError && (
                                <div className="alert alert-danger d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="fw-bold">
                                      Stripe invoicing failed
                                      {invoice.stripeErrorUtc &&
                                        ` on ${Moment.utc(
                                          invoice.stripeErrorUtc.substring(0, 19)
                                        )
                                          .tz(Moment.tz.guess())
                                          .format("DD MMM YYYY HH:mm:ss")}`}
                                    </div>
                                    <div>{invoice.stripeError}</div>
                                    <div className="small">
                                      This invoice won't be retried automatically.
                                    </div>
                                  </div>
                                  <button
                                    className="btn btn-danger ms-3"
                                    disabled={retrying}
                                    onClick={() => retryInvoice(invoice.id)}
                                  >
                                    {retrying ? "Retrying..." : "Retry"}
                                  </button>
                                </div>
                              )}
                              <div className="row">
                                <div className="col-md-3">
                                  <h5 className="card-title">
                                    Amount: ${invoice?.amount.toFixed(2)}
                                  </h5>
                                  <p className="card-text">
                                    Status: {invoice?.status}
                                  </p>
                                  {invoice?.stripeInvoicePdfUrl && (
                                    <a
                                      href={invoice?.stripeInvoicePdfUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Download PDF
                                    </a>
                                  )}
                                </div>
                                <div className="col-md-9">
                                  <table className="table">
                                    <thead>
                                      <tr>
                                        <th>Description</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {invoice?.invoiceItems?.map(
                                        (item, index) => (
                                          <tr key={index}>
                                            <td>{item?.description}</td>
                                            <td>{item?.quantity}</td>
                                            <td>
                                              ${item?.unitPrice.toFixed(2)}
                                            </td>
                                            <td>${item?.amount.toFixed(2)}</td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {transaction.behaviorViolations &&
            transaction.behaviorViolations.length > 0 && (
              <div className="row p-3 border rounded bg-light mb-3">
                <div className="col-md-12 fw-bold mb-2">
                  Behavior Violations:
                </div>
                <div className="col-md-12">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Description</th>
                        <th>Resolved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transaction.behaviorViolations.map((violation) => {
                        const isResolved = violation?.resolvedUtc !== null;
                        return (
                          <tr
                            key={violation?.id}
                            className={
                              isResolved ? "table-success" : "table-danger"
                            }
                          >
                            <td>
                              {violation?.detectedUtc ? (
                                <ShareparkDateTime
                                  timestamp={violation.detectedUtc}
                                />
                              ) : (
                                ""
                              )}
                            </td>
                            <td>{violation?.violationType}</td>
                            <td>{violation?.status}</td>
                            <td>{violation?.description}</td>
                            <td>
                              {violation?.resolvedUtc ? (
                                <ShareparkDateTime
                                  timestamp={violation.resolvedUtc}
                                />
                              ) : (
                                "Not Resolved"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {transaction.events && transaction.events.length > 0 && (
            <>
              <div className="row p-3 border rounded bg-light mb-3">
                <div className="col-md-12 fw-bold mb-2">Events:</div>
                <div className="col-md-12">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Type</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transaction.events.map((event) => (
                        <tr key={event?.id}>
                          <td>
                            {Moment.utc(event?.createdUtc?.substring(0, 19))
                              .tz(Moment.tz.guess())
                              .format("DD MMM YYYY HH:mm:ss")}
                          </td>
                          <td>{event?.type}</td>
                          <td>{event?.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {transaction.siteMessages && transaction.siteMessages.length > 0 && (
            <>
              <div className="row p-3 border rounded bg-light mb-3">
                <div className="col-md-12 fw-bold mb-2">Site Messages:</div>
                <div className="col-md-12">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Type</th>
                        <th>Payload</th>
                        <th>Response</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transaction.siteMessages.map((siteMessage) => (
                        <tr key={siteMessage?.id}>
                          <td>
                            {Moment.utc(
                              siteMessage?.createdUtc?.substring(0, 19)
                            )
                              .tz(Moment.tz.guess())
                              .format("DD MMM YYYY HH:mm:ss")}
                          </td>
                          <td>{siteMessage?.type}</td>
                          <td>
                            <ReactJson
                              name={null}
                              src={JSON.parse(siteMessage?.payload || "{}")}
                              collapsed={1}
                              style={{ fontSize: "0.75rem" }}
                            />
                          </td>
                          <td>
                            <ReactJson
                              name={null}
                              src={JSON.parse(siteMessage?.response || "{}")}
                              collapsed={1}
                              style={{ fontSize: "0.75rem" }}
                            />
                          </td>
                          <td>{siteMessage?.duration} ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
